import {Injectable} from '@angular/core';
import {AxisLabelsFormatterContextObject, AxisPlotBandsOptions, XAxisOptions, YAxisOptions} from 'highcharts';
import {ChartConstants} from '@constants/pages/chart-constants';
import {ChartsHelperService} from '@services/charts/helper/charts-helper.service';
import {DatePipe, DecimalPipe} from '@angular/common';
import {TranslationService} from '@services/translation/translation.service';

@Injectable({
  providedIn: 'root'
})
export class ChartAxisSettingsGeneratorService {

  constructor(private chartsHelperService: ChartsHelperService,
              private decimalPipe: DecimalPipe,
              private datePipe: DatePipe,
              private translationService: TranslationService) {
  }

  public createAllYAxis(isLineChart, machineSignals, chartSettings) {
    //Create list of axes, with default axes at first index

    const signals = chartSettings.signals;
    let axisCount = signals ? signals.length : 0;
    if (isLineChart) {
      axisCount = chartSettings.axis ? chartSettings.axis.length : 0;
    }
    const axisArray: YAxisOptions[] = [];
    if (axisCount === 0) {
      return axisArray;
    }
    if (!isLineChart) {
      //default axis
      axisArray.push(this.createSingleYAxis(isLineChart, 'default', ChartConstants.DEFAULT_LINE_COLOR, false, {}));
    }
    for (let i = 0; i < axisCount; i++) {
      let label: string = '';
      let color: string = '';
      let isOpposite: boolean = false;
      let axisSignalSettings;
      if (isLineChart) {
        const axis = chartSettings.axis[i];
        label = axis.axisYLabel;
        color = this.chartsHelperService.determineLineChartAxisColor(axis, isLineChart);
        isOpposite = axis.position === 'RIGHT';
        axisSignalSettings = {...axis};
      } else {
        label = this.createAxisLabel(this.translationService.translate.instant(signals[i].axisYLabel), signals[i].unit);
        color = signals[i].colors ? this.chartsHelperService.createHex(signals[i].colors.main, isLineChart) : ChartConstants.DEFAULT_LINE_COLOR;
        isOpposite = signals[i].axis === 'RIGHT';
        axisSignalSettings = {...signals[i]};
      }
      axisArray.push(this.createSingleYAxis(isLineChart, label, color, isOpposite, axisSignalSettings));
    }
    return axisArray;
  }

  public createXAxis(isLineChart: boolean, chartSettings, data: any[], stringData: any[], language: string): XAxisOptions {
    return {
      crosshair: true,
      gridLineWidth: isLineChart ? 0 : 1,
      type: 'datetime',
      labels: {
        overflow: 'justify',
        formatter: (label: AxisLabelsFormatterContextObject) => {
          const date = new Date(label.value);
          const pattern = this.chartsHelperService.getDateTimeFormatPattern(isLineChart, data, language, chartSettings);
          return this.datePipe.transform(date, pattern, undefined, this.translationService.getActiveLanguage()) as string;
        }
      },
      plotBands: this.generatePlotBands(stringData),
      events: {
        afterSetExtremes: (xAxis) => {
          // this.togglePlotBands(xAxis);
        }
      }
    };

  }

  public togglePlotBands(xAxis) {
    if (!xAxis) {
      return;
    }

    const plotLeft = xAxis[0].chart.plotLeft;
    const plotWidth = xAxis[0].chart.plotWidth;
    xAxis[0].plotLinesAndBands.forEach(function (plotBandItem) {
      const from = Math.max(xAxis[0].toPixels(plotBandItem.options.from, true), plotLeft);
      const to = Math.min(xAxis[0].toPixels(plotBandItem.options.to, true), plotLeft + plotWidth);

      const plotBandWidth = to - from;

      if (plotBandItem.label) {
        const show = plotBandItem.label.getBBox().width < plotBandWidth;

        plotBandItem.label.css({opacity: Number(show)});
      }
    });

  }

  private generatePlotBands(stringData: any[]) {
    const plotBandsArray: any[] = [];
    if (stringData && stringData.values && stringData.values.length > 0) {

      for (let k = 0; k < stringData.values.length; k++) {
        plotBandsArray.push(this.generatePlotBand(k, stringData.values[k][0], stringData.values[k][1], stringData.values[k][2]));
      }
    }
    return plotBandsArray;
  }

  private generatePlotBand(k, from, to, label): AxisPlotBandsOptions {
    return {
      id: 'plot-band-ida-' + k,
      borderWidth: 2,
      borderColor: '#999999',
      from: from,
      to: to,
      color: '#f7f7f7',
      label: {
        text: label,
        style: {
          color: '#999999'
        }
      }
    };
  }

  private createSingleYAxis(isLineChart, label: string, color: string, isOpposite: boolean, settings: any): YAxisOptions {

    let axis = {
      crosshair: false,
      gridLineWidth: isLineChart ? 1 : 0,
      opposite: isOpposite,
      showEmpty: false,
      lineColor: color,
      lineWidth: 1,
      title: {
        text: label,
        style: {
          color: color
        }
      },
      labels: {
        format: '{value:,.f}',
        style: {
          color: color,
        }
      },

      startOnTick: false,
      endOnTick: false,

    };

    axis = this.handleYAxisCustomRange(isLineChart, settings, axis);

    return axis;

  }

  private createAxisLabel(label, unit) {
    return label ? label + (unit ? ' [' + unit + ']' : '') : '';
  }

  private handleYAxisCustomRange(isLineChart, settings, axis) {
    if ((!isLineChart && settings.customYAxisInterval === true) && this.isDefined(settings.minY) && this.isDefined(settings.maxY) &&
        Number(settings.maxY) > Number(settings.minY)) {

      axis = this.chartsHelperService.verticalAxisCustomRange(settings, axis);

    } else if (isLineChart && (!this.isDefaultValues(settings.minY, settings.maxY)) && Number(settings.maxY) > Number(settings.minY)) {
      axis = this.chartsHelperService.verticalAxisCustomRange(settings, axis);
    } else {
      axis.tickAmount = ChartConstants.LINE_CHARTS_TICK_INTERVALS + 1;
    }

    return axis;

  }

  private isDefaultValues(value1, value2) {
    return value1 === 0 && value2 === 0;
  }

  private isDefined(value) {
    return typeof value !== 'undefined';
  }

}
